import React from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import {
  hasTenantPermission,
  isSuperAdminRole,
  isTenantAdminRole,
} from "@/utils/accessControl";
import { PageState } from "@/components/common/PageState";

interface TenantPermissionGuardProps {
  /**
   * Required tenant permission. Pass an array to allow access when the user
   * holds ANY of the listed permissions (e.g. a read-only "View" role and a
   * full "administrate" role can both reach a detail screen).
   */
  permission: string | readonly string[];
  children: React.ReactNode;
  fallbackPath?: string;
}

export const TenantPermissionGuard: React.FC<TenantPermissionGuardProps> = ({
  permission,
  children,
  fallbackPath,
}) => {
  const { t } = useTranslation();
  const { userProfile, isLoading } = useAuthenticatedUser();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  const requiredPermissions = Array.isArray(permission)
    ? permission
    : [permission];

  // Custom tenant permissions only constrain the Tenant Admin scope. Hotel
  // roles are governed by their dedicated role/core-feature guards and do not
  // carry the tenant custom-permissions claim.
  if (!isTenantAdminRole(userProfile) || isSuperAdminRole(userProfile)) {
    return <>{children}</>;
  }

  const hasRequiredPermission = requiredPermissions.some((required) =>
    hasTenantPermission(userProfile, required),
  );

  if (!hasRequiredPermission) {
    if (fallbackPath) {
      return <Navigate replace to={fallbackPath} />;
    }

    return (
      <PageState
        tone="error"
        text={t("common.insufficientPermissions", "Insufficient permissions.")}
      />
    );
  }

  return <>{children}</>;
};
